import {
  type OptionWithValue,
  type OptionWithValueAndOid,
} from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import { DragToSort } from '../../../../../drag-to-sort.gts';
import { OptionsPresets } from './options-presets.gts';
import { OptionsWizard } from './options-wizard.gts';

export interface EditOptionsSignature<T extends OptionWithValueAndOid> {
  Args: {
    uniqueId: string;
    options: T[];
    addOption: (option?: OptionWithValue) => void;
    setPresets: (options: OptionWithValue[]) => void;
    updateOption: (option: T, event: Event) => void;
    removeOptionAt: (index: number) => void;
    updateOptions: (options: T[]) => void;
    hasScore?: boolean;
  };
  Element: HTMLDivElement;
}

function addOne(index: number): number {
  return index + 1;
}

export class EditOptions<T extends OptionWithValueAndOid> extends Component<
  EditOptionsSignature<T>
> {
  OptionsWizard = OptionsWizard<T>;

  <template>
    <div ...attributes>
      <label>Options</label>
      {{#if @options.length}}
        <div class="list-group mb-3 gap-1">
          <DragToSort @items={{@options}} @updateItems={{@updateOptions}}>
            <:item as |dropZone draggable option index|>
              <div class="input-group" {{dropZone index}}>
                <span
                  class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                  {{draggable option index}}
                  title="Drag to reorder"
                >
                  {{addOne index}}
                </span>
                {{! template-lint-disable require-input-label }}
                <input
                  type="text"
                  class="form-control list-group-item"
                  value={{option.value}}
                  aria-label="Option value"
                  id="{{@uniqueId}}-option-{{option.oid}}"
                  {{on "change" (fn @updateOption option)}}
                />
                <button
                  aria-label="Remove option"
                  class="btn btn-outline-primary rounded-end-1"
                  type="button"
                  {{on "click" (fn @removeOptionAt index)}}
                ><iconify-icon inline icon="ph:trash-bold" /></button>
              </div>
            </:item>
          </DragToSort>
        </div>
      {{else}}
        <p>Click the button below to add an option.</p>
      {{/if}}

      <div class="d-flex gap-2">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          {{on "click" (fn @addOption undefined)}}
        >
          <iconify-icon inline icon="ph:plus-bold" />
          Add Option
        </button>

        {{#if @options.length}}
          <this.OptionsWizard
            @options={{@options}}
            @updateOptions={{@updateOptions}}
            @hasScore={{@hasScore}}
          />
        {{else}}
          <OptionsPresets @updateOptions={{@setPresets}} />
        {{/if}}
      </div>
    </div>
  </template>
}
